import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Users, Check, Clock, X, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { useCourtParticipantsAttendance, AttendanceStatus } from '@/hooks/useCourtParticipantsAttendance';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CourtParticipantsListProps {
  eventId: string;
  encounterNumber: number;
}

const getAttendanceBadge = (status: AttendanceStatus) => {
  switch (status) {
    case 'confirmed':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
          <Check className="w-3 h-3" />
          Confirmado
        </Badge>
      );
    case 'not_attending':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1">
          <X className="w-3 h-3" />
          No asiste
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pendiente
        </Badge>
      );
  }
};

const getMovementBadge = (label: string, type: string) => {
  switch (type) {
    case 'up':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
          <ArrowUp className="w-3 h-3" />
          {label}
        </Badge>
      );
    case 'down':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1">
          <ArrowDown className="w-3 h-3" />
          {label}
        </Badge>
      );
    case 'neutral':
    default:
      return (
        <Badge className="bg-muted text-muted-foreground hover:bg-muted flex items-center gap-1">
          <Minus className="w-3 h-3" />
          {label}
        </Badge>
      );
  }
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const CourtParticipantsList: React.FC<CourtParticipantsListProps> = ({
  eventId,
  encounterNumber
}) => {
  const [selectedCourt, setSelectedCourt] = useState<number | null>(null);
  const { participants, courtNumber, userCourtNumber, availableCourts, isLoading } = useCourtParticipantsAttendance(
    eventId,
    encounterNumber,
    selectedCourt
  );

  // Default to user's court once known
  useEffect(() => {
    if (selectedCourt == null && userCourtNumber != null) {
      setSelectedCourt(userCourtNumber);
    }
  }, [userCourtNumber, selectedCourt]);

  // Reset selection when encounter changes
  useEffect(() => {
    setSelectedCourt(null);
  }, [encounterNumber, eventId]);

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (participants.length === 0 && availableCourts.length === 0) {
    return null;
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
          <Users className="w-5 h-5 text-primary" />
          <span>Participantes</span>
          {availableCourts.length > 1 ? (
            <Select
              value={String(courtNumber ?? '')}
              onValueChange={(v) => setSelectedCourt(Number(v))}
            >
              <SelectTrigger className="h-8 w-[160px] text-sm font-normal">
                <SelectValue placeholder="Cancha" />
              </SelectTrigger>
              <SelectContent>
                {availableCourts.map((c) => (
                  <SelectItem key={c} value={String(c)}>
                    Cancha {c}{c === userCourtNumber ? ' (la tuya)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm font-normal text-muted-foreground">
              Cancha {courtNumber || 1}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {[...participants]
          .map((p) => ({ p, dj: (p.jf || 0) - (p.jc || 0) }))
          .sort((a, b) => {
            const rank = (t?: string) => (t === 'up' ? 0 : t === 'down' ? 2 : 1);
            const ra = rank(a.p.movementType);
            const rb = rank(b.p.movementType);
            if (ra !== rb) return ra - rb;
            return b.dj - a.dj;
          })
          .map(({ p: participant, dj }, index) => {
            const jj = participant.jj || 0;
            const pg = participant.pg || 0;
            const jf = participant.jf || 0;
            const jc = participant.jc || 0;
            const losses = Math.max(0, jj - pg);
            const isUser = !!(participant as any).isCurrentUser;
            const mt = participant.movementType;
            const formatDiff = (v: number) => (v > 0 ? `+${v}` : `${v}`);
            const diffColor = (v: number) =>
              v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : 'text-muted-foreground';

            const borderColor = isUser
              ? 'border-l-primary'
              : mt === 'up'
              ? 'border-l-green-500'
              : mt === 'down'
              ? 'border-l-red-500'
              : 'border-l-gray-300';
            const bgColor = isUser
              ? 'bg-primary/5'
              : mt === 'up'
              ? 'bg-green-50'
              : mt === 'down'
              ? 'bg-red-50'
              : 'bg-white';

            return (
              <div
                key={`${participant.playerName}-${index}`}
                className={`p-4 rounded-lg border-l-4 shadow-sm border ${borderColor} ${bgColor} border-gray-200`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="font-bold text-base text-gray-900 flex-1">
                    {participant.playerName}
                  </div>
                  <div className={`text-3xl font-bold ml-4 ${diffColor(dj)}`}>
                    {formatDiff(dj)}
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-1">
                  PJ: <span className="font-medium">{jj}</span>
                  <span className="mx-2">|</span>
                  G: <span className="font-medium text-green-600">{pg}</span>
                  <span className="mx-2">|</span>
                  P: <span className="font-medium text-red-600">{losses}</span>
                </div>

                <div className="text-sm text-gray-600 mb-2">
                  Juegos: <span className="font-medium">{jf}-{jc}</span>
                  <span className={`ml-1 ${diffColor(dj)}`}>({formatDiff(dj)})</span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">
                    Posición: {index + 1}º
                  </span>
                  {participant.movementLabel && participant.movementType &&
                    getMovementBadge(participant.movementLabel, participant.movementType)}
                  
                  {isUser && (
                    <Badge className="bg-primary/10 text-primary text-xs">Tú</Badge>
                  )}
                </div>
              </div>
            );
          })}
      </CardContent>
    </Card>
  );
};

export default CourtParticipantsList;
